import { useState, useEffect, useCallback } from "react";

export interface ModulePermission {
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

export type PermissionsMap = Record<string, ModulePermission>;

const STORAGE_KEY = "user_permissions";
const ROLE_KEY    = "user_role";
const USER_ID_KEY = "user_id";

export const savePermissions = (permissions: PermissionsMap, role: string, userId?: number) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(permissions));
  localStorage.setItem(ROLE_KEY, role);
  if (typeof userId === "number") {
    localStorage.setItem(USER_ID_KEY, String(userId));
  }
};

export const clearPermissions = () => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(ROLE_KEY);
  localStorage.removeItem(USER_ID_KEY);
};

export const getStoredPermissions = (): PermissionsMap => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

export const getStoredRole = (): string => {
  return localStorage.getItem(ROLE_KEY) || "";
};

/** Currently authenticated user's primary key (from /user/userInfo).
 *  Returns 0 when unauthenticated or unsaved — callers should treat 0
 *  as "no user" rather than "user with id 0". */
export const getStoredUserId = (): number => {
  const raw = localStorage.getItem(USER_ID_KEY);
  const n   = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(n) ? n : 0;
};

export const usePermission = () => {
  const [permissions, setPermissions] = useState<PermissionsMap>(getStoredPermissions);
  const [role,        setRole]        = useState<string>(getStoredRole);
  const [userId,      setUserId]      = useState<number>(getStoredUserId);

  useEffect(() => {
    const handleStorage = () => {
      setPermissions(getStoredPermissions());
      setRole(getStoredRole());
      setUserId(getStoredUserId());
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const updatePermissions = useCallback((perms: PermissionsMap, roleName: string, uid?: number) => {
    savePermissions(perms, roleName, uid);
    setPermissions(perms);
    setRole(roleName);
    if (typeof uid === "number") setUserId(uid);
  }, []);

  const isAdmin        = role === "Admin";
  const isSalesManager = role === "Sales Manager";
  /** Roles permitted to see / reassign every lead in the system. */
  const canViewAllLeads = isAdmin || isSalesManager;

  /* ── Financial-visibility gate ────────────────────────────────────────
   * Roles permitted to see revenue / profit / margin / deal-size widgets
   * on the dashboard. Every other role gets an operational-only dashboard
   * (counts and follow-up metrics) per the role-based UI spec.
   *
   * NOTE: System Admin / Super Admin both collapse to role === "Admin" on
   * the frontend — see UserController::userInfo() where the legacy
   * `super_admin` driver role is mapped to roleName "Admin" before the
   * response is returned. So the single "Admin" check covers both. */
  const FINANCIAL_ROLES = new Set<string>([
    "Admin",
    "Finance Manager",
    "Sales Director",
    "Operations Manager",
  ]);
  const canViewFinancials = FINANCIAL_ROLES.has(role);

  /* ── Company-wide dashboard gate ─────────────────────────────────────
   * Privileged roles see EVERY user's data on the dashboard (totals,
   * pipeline, calendar, recent activity). Everyone else — including
   * Sales Manager — sees only their personal slice: leads assigned to
   * them OR created by them, follow-ups they own, activities they
   * authored.
   *
   * Currently the role set matches FINANCIAL_ROLES exactly. If the two
   * concerns ever diverge (e.g. an Operations Manager who sees team
   * activity but not revenue), split this into its own role list. */
  const canViewCompanyDashboard = canViewFinancials;

  const hasPermission = useCallback(
    (module: string, action: "view" | "create" | "edit" | "delete") => {
      if (isAdmin) return true;
      const perm = permissions[module];
      if (!perm) return false;
      return perm[`can_${action}`] ?? false;
    },
    [permissions, isAdmin]
  );

  const canView   = useCallback((module: string) => hasPermission(module, "view"),   [hasPermission]);
  const canCreate = useCallback((module: string) => hasPermission(module, "create"), [hasPermission]);
  const canEdit   = useCallback((module: string) => hasPermission(module, "edit"),   [hasPermission]);
  const canDelete = useCallback((module: string) => hasPermission(module, "delete"), [hasPermission]);

  return {
    permissions, role, userId,
    isAdmin, isSalesManager, canViewAllLeads, canViewFinancials, canViewCompanyDashboard,
    hasPermission, canView, canCreate, canEdit, canDelete,
    updatePermissions,
  };
};
